/**
 * Pure helper functions and constants for the Reports module.
 * Extracted from ReportsPage.js for maintainability.
 * No React dependencies — these are pure data transformations.
 */
import { parseISO, eachDayOfInterval } from 'date-fns';

// ── Status Constants ──

export const statusColors = {
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  completed_delayed: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-800',
  in_progress: 'bg-amber-100 text-amber-800'
};

export const statusLabels = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  completed_delayed: 'Completed (Delayed)',
  cancelled: 'Cancelled',
  in_progress: 'In Progress'
};

export const leaveColors = {
  'LEAVE_HOLIDAY': { bg: '#fef3c7', border: '#f59e0b' },
  'LEAVE_SICK': { bg: '#fee2e2', border: '#ef4444' },
  'LEAVE_COMPASSIONATE': { bg: '#f3e8ff', border: '#a855f7' },
  'LEAVE_OTHER': { bg: '#f1f5f9', border: '#64748b' }
};

// ── Pure Functions ──

/** Determine display status (Phase 1A - delayed without justification → amber) */
export function getDisplayStatus(job) {
  if (job.status === 'completed') {
    const isDelayed = job.days_to_complete !== null && job.days_to_complete > 1;
    const isJustified = job.delay_justified === true;
    if (isDelayed && !isJustified) {
      return 'completed_delayed';
    }
  }
  return job.status;
}

/** Get customer info for a job */
export function getJobCustomer(job) {
  return job.site?.parent_client || job.site?.independent_client || null;
}

/** Get display label for a job (handles leave events) */
export function getJobLabel(job) {
  if (job.event_type?.startsWith('LEAVE')) {
    const labels = {
      'LEAVE_HOLIDAY': '\u{1F3D6}\uFE0F Holiday',
      'LEAVE_SICK': '\u{1F912} Sick',
      'LEAVE_COMPASSIONATE': '\u{1F49C} Compassionate',
      'LEAVE_OTHER': `\u{1F4CB} ${job.notes || 'Other'}`
    };
    return labels[job.event_type] || 'Leave';
  }
  return job.site?.name || job.service?.name || `Job #${job.id}`;
}

/** Get total span of a job in days (legacy: job-level) */
export function getJobSpan(job) {
  if (job.job_dates && job.job_dates.length > 0) {
    return job.job_dates.length;
  }
  if (!job.end_date || job.end_date === job.scheduled_date) return 1;
  const start = parseISO(job.scheduled_date);
  const end = parseISO(job.end_date);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

/** Get job span for a specific auditor (supports per-auditor assignments) */
export function getJobSpanForAuditor(job, auditorId) {
  if (job.assignments && job.assignments.length > 0) {
    // iter453 — an auditor can have multiple non-contiguous assignment rows;
    // sum days across ALL of them so split dates are counted correctly.
    const rows = job.assignments.filter(a => a.auditor_id === auditorId);
    if (rows.length > 0) {
      return rows.reduce((total, r) => {
        const s = parseISO(r.start_date);
        const e = parseISO(r.end_date);
        return total + Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    }
  }
  if (job.job_dates && job.job_dates.length > 0) {
    return job.job_dates.length;
  }
  if (!job.end_date || job.end_date === job.scheduled_date) return 1;
  const start = parseISO(job.scheduled_date);
  const end = parseISO(job.end_date);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

/** Check if this is the first day of a multi-day job for this auditor */
export function isFirstDayOfJob(job, dateStr, auditorId) {
  if (job.assignments && job.assignments.length > 0) {
    // iter453 — a job may have multiple assignment rows for one auditor
    // (split dates). `dateStr` is a "first day" if it matches the
    // start_date of ANY of that auditor's rows.
    const rows = job.assignments.filter(a => a.auditor_id === auditorId);
    if (rows.length > 0) {
      return rows.some(r => r.start_date === dateStr);
    }
  }
  if (job.job_dates && job.job_dates.length > 0) {
    const sortedDates = [...job.job_dates].sort();
    return sortedDates[0] === dateStr;
  }
  return job.scheduled_date === dateStr;
}

/** Get print scale class based on number of auditors */
export function getPrintScaleClass(auditorCount) {
  if (auditorCount <= 10) return '';
  if (auditorCount <= 15) return 'print-scale-sm';
  return 'print-scale-xs';
}

// ── Parameterized Functions (were closures in the original) ──

/** Get jobs for a specific auditor on a specific date */
export function getJobsForAuditorDate(jobs, auditorId, dateStr) {
  return jobs.filter(job => {
    const hasAuditor = job.auditors?.some(a => a.id === auditorId);
    if (!hasAuditor) return false;

    // Check per-auditor assignments first (new system for complex jobs)
    if (job.assignments && job.assignments.length > 0) {
      // iter453 — collect ALL assignment rows for this auditor (multi-range
      // support) and return true if ANY covers the check date. Previously
      // `.find()` only returned the first row so split-date jobs (e.g.
      // Sophie/Holiday Inn Corby 14+15 Jul) rendered on only one day.
      const rows = job.assignments.filter(a => a.auditor_id === auditorId);
      if (rows.length > 0) {
        return rows.some(r => dateStr >= r.start_date && dateStr <= r.end_date);
      }
      // If there are assignments but none for this auditor, don't show
      return false;
    }

    // Check for non-consecutive dates (legacy job_dates system)
    if (job.job_dates && job.job_dates.length > 0) {
      return job.job_dates.includes(dateStr);
    }

    // Fall back to consecutive date range (legacy jobs)
    const jobStart = job.scheduled_date;
    const jobEnd = job.end_date || job.scheduled_date;
    return dateStr >= jobStart && dateStr <= jobEnd;
  });
}

/** Filter auditors to only those with jobs in the current data set */
export function getActiveAuditorsWithJobs(jobs, auditors) {
  const auditorIdsWithJobs = new Set();
  jobs.forEach(job => {
    job.auditors?.forEach(a => auditorIdsWithJobs.add(a.id));
  });
  return auditors.filter(a => auditorIdsWithJobs.has(a.id));
}

/** Generate date range for matrix views */
export function getDateRange(startDate, endDate) {
  return eachDayOfInterval({
    start: parseISO(startDate),
    end: parseISO(endDate)
  });
}

/** Check if a date string is a bank holiday */
export function getBankHoliday(bankHolidays, dateStr) {
  return bankHolidays.find(h => h.date === dateStr);
}

/** Export data to a CSV file download */
export function exportToCSV(data, filename, headers) {
  const csvContent = [
    headers.join(','),
    ...data.map(row => row.map(cell => {
      const str = String(cell || '');
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}
